import regEx from 'utils/global/regEx'
import { toggleEditableBlocks } from '../updateDynamicText/toggleEditableBlocks'
import { t } from 'i18next'

type OutlineStyle = `${number}px`
type OutlineWidth = OutlineStyle | 'remove'
type OutlineColor = string
type OutlineResult = {
  width: OutlineStyle
  color: OutlineColor
}

const DEFAULT_OUTLINE: OutlineResult = {
  width: '1px' as const,
  color: 'black' as const,
} as const

// Outline stroke sizes for the menu
const OUTLINE_STROKE_SIZES: OutlineStyle[] = Array.from(
  { length: 10 },
  (_, i) => `${i + 1}px` as const,
)

export const registerTextOutlinePlugin = ({ editor }: { editor: Editor }) => {
  const { unlockNonEditableBlocks, lockNonEditableBlocks } =
    toggleEditableBlocks(editor)

  // misspelled words should allow outlines to be applied
  // This event listener runs orthogonally with applyStylingToNonEditable's NodeChange event listeners
  editor.on('NodeChange', () => {
    const node = editor.selection.getNode()
    const outlinedElement = node.classList?.contains('outline')
      ? node
      : node.closest('.outline')
    if (outlinedElement) {
      const blockParent = outlinedElement.closest('p')
      if (blockParent) {
        editor.dom.setAttribs(blockParent, { spellcheck: 'false' })
      }
      editor.dom.setAttribs(outlinedElement, { spellcheck: 'false' })
      outlinedElement.querySelectorAll('*').forEach((el: Element) => {
        editor.dom.setAttribs(el, { spellcheck: 'false' })
      })
    }
  })

  // Helper function to get the current text-stroke style of the selection
  const getTextOutline = (): OutlineResult => {
    const node = editor.selection.getNode()

    const isValidColor = (color: string) => {
      return (
        color.match(regEx.colorFormatIdentifier) ||
        color.match(regEx.lettersOnly)
      )
    }

    const getStyle = (node: Element, style: string) =>
      editor.dom.getStyle(node, style)
    const checkNodeStyles = (node: Element): OutlineResult | null => {
      // 1. Check direct styles
      const stroke = getStyle(node, '-webkit-text-stroke')
      if (stroke?.includes('px')) {
        const strokeParts = stroke.split(' ')
        const width = strokeParts[0]
        const color = strokeParts.slice(1).join(' ')
        if (isValidColor(color)) {
          return { width, color } as OutlineResult
        }
      }

      // 2. Check self or parents with outline class
      const outlineElement = node.classList?.contains('outline')
        ? node
        : node.closest('.outline')
      if (outlineElement) {
        const computed = window.getComputedStyle(outlineElement)
        const computedStroke = computed.getPropertyValue('-webkit-text-stroke')
        if (computedStroke?.includes('px')) {
          const strokeParts = computedStroke.split(' ')
          const width = strokeParts[0]
          const color = strokeParts.slice(1).join(' ')
          if (isValidColor(color)) {
            return { width, color } as OutlineResult
          }
        }
      }

      // 3. Check children with outline class
      const outlineChild = node.querySelector('.outline')
      if (outlineChild) {
        const computed = window.getComputedStyle(outlineChild)
        const computedStroke = computed.getPropertyValue('-webkit-text-stroke')
        if (computedStroke?.includes('px')) {
          const strokeParts = computedStroke.split(' ')
          const width = strokeParts[0]
          const color = strokeParts.slice(1).join(' ')
          if (isValidColor(color)) {
            return { width, color } as OutlineResult
          }
        }
      }

      return null
    }

    const result = checkNodeStyles(node)

    return result || DEFAULT_OUTLINE
  }

  const removeTextOutlineFromParent = (node: Element) => {
    const parent = node.closest('p')
    if (parent) {
      removeTextOutlineRecursive(parent)
    } else {
      removeTextOutlineRecursive(node)
    }
  }

  // Helper function to recursively remove the text-stroke from all child nodes
  const removeTextOutlineRecursive = (node: HTMLParagraphElement | Element) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      editor.dom.setStyle(node, '-webkit-text-stroke', null)
      editor.dom.setStyle(node, 'text-stroke', null)
      editor.dom.removeClass(node, 'outline')
      editor.dom.setAttribs(node, { spellcheck: 'true' })
      Array.from(node.children).forEach(removeTextOutlineRecursive)
    }
  }

  // Apply or remove the text-stroke style. The undo transaction records the
  // change as an undo level, which marks the editor dirty: a box writes its
  // HTML back on blur only when it is dirty (shouldCommitBlurContent).
  const applyTextOutline = (
    outlineWidth: OutlineWidth,
    color: OutlineColor,
  ) => {
    editor.formatter.register('customTextOutline', {
      inline: 'span',
      classes: ['outline'],
      styles: {
        '-webkit-text-stroke': `${outlineWidth} ${color}`,
        'text-stroke': `${outlineWidth} ${color}`,
      },
      attributes: {
        spellcheck: 'false',
      },
    })

    editor.undoManager.transact(() => {
      unlockNonEditableBlocks()

      if (outlineWidth === 'remove') {
        editor.formatter.remove('customTextOutline')
        removeTextOutlineFromParent(editor.selection.getNode())
      } else {
        editor.formatter.apply('customTextOutline', {
          value: `${outlineWidth} ${color}`,
        })
      }

      lockNonEditableBlocks()
    })
  }

  // Add a button for removing the outline
  editor.ui.registry.addButton('removeOutline', {
    text: t('editor.tinyMCE.textOutline.removeOutline'),
    onAction: () => {
      applyTextOutline('remove', 'black')
    },
  })

  // Add a menu button to select outline width
  editor.ui.registry.addMenuButton('outline', {
    text: t('editor.tinyMCE.textOutline.text'),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fetch: (callback: (items: any[]) => void) => {
      const currentOutline = getTextOutline()
      const items = OUTLINE_STROKE_SIZES.map((outline) => ({
        type: 'menuitem' as const,
        text: `${outline}`,
        onAction: () => applyTextOutline(outline, currentOutline.color),
        active: currentOutline.width === outline,
      }))

      // Add an option to choose a custom outline color
      items.push({
        type: 'menuitem',
        text: t('editor.tinyMCE.textOutline.chooseOutlineText'),
        active: false,
        onAction: () => {
          // Open color picker dialog
          editor.windowManager.open({
            title: t('editor.tinyMCE.textOutline.modalTitle'),
            initialData: {
              outlineColor: currentOutline.color,
            },
            body: {
              type: 'panel',
              items: [
                {
                  type: 'colorinput',
                  name: 'outlineColor',
                  label: t('editor.tinyMCE.textOutline.modalLabelWithHint'),
                },
              ],
            },
            buttons: [
              {
                type: 'cancel',
                name: 'cancel',
                text: t('editor.tinyMCE.textOutline.cancelButton'),
              },
              {
                type: 'submit',
                name: 'submit',
                text: t('editor.tinyMCE.textOutline.applyButton'),
                primary: true,
              },
            ],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onSubmit: (api: any) => {
              const data = api.getData()
              applyTextOutline(currentOutline.width, data.outlineColor)
              api.close()
            },
          })
        },
      })

      // Add an option to remove the outline
      items.push({
        type: 'menuitem',
        text: t('editor.tinyMCE.textOutline.removeOutlineText'),
        onAction: () => applyTextOutline('remove', 'black'),
        active: !currentOutline.width,
      })

      callback(items)
    },
  })
}

export default registerTextOutlinePlugin
